// Field Lines folder builder.
//
// Layout (top → bottom): Show toggle, Step Size, Max Steps, Opacity,
// (dev-only display controls), Trace/Clear square action tiles, and a Domain
// tab-strip footer.
//
// Field lines are an *accumulating* artifact, not a single live render: each
// Trace seeds a streamline from the draggable selection cube and adds it to the
// scene; Clear wipes them all. So the domain strip is scope-only (like Contour,
// unlike Volume's glow toggle) — it bounds where the *next* trace seeds and
// terminates, with no on/off light. A Box/Sphere domain trims the trace to its
// bounds in `traceFromSelection()`; `Full` means no spatial restriction.
//
// The action tiles and domain strip are raw DOM appended to the folder body, so
// they survive pane child disposal between rebuilds. They are cached on the
// folder (mirroring Contour) and re-pinned to the body bottom on every rebuild.

import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { buildActionTiles, syncVisibilityTile } from './actionTiles';
import { buildDomainStrip, renderDomainStrip, syncDomainSelected, type DomainStripRefs } from './domainStrip';
import { bindSlider } from '../sliderTicks';
import type { TPaneLike, TFolderLike, Refs } from '../types';

interface FieldLinesCtx {
    params: Record<string, unknown>;
    developerMode?: boolean;
}

interface FieldLinesCustomRefs {
    actions: HTMLDivElement;
    domain: DomainStripRefs;
    offRefresh: () => void;
}

// Cached on the folder so the raw-DOM footer (action tiles + domain strip) and
// the UI_PANE_REFRESH listener survive full rebuilds — the folder element is
// stable across calls; only pane children get disposed and recreated.
const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, FieldLinesCustomRefs>();

export function buildFieldLinesSection(paneArg: unknown, refs: Refs, ctx: FieldLinesCtx): Refs {
    const pane = paneArg as TPaneLike;
    const { params, developerMode } = ctx;
    let folder = refs['fieldLinesFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Field Lines', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'fieldlines';
        refs['fieldLinesFolderRef'] = folder;
    } else {
        try { const children = Array.isArray(folder.children) ? folder.children.slice() : []; children.forEach((c) => { try { (c as TFolderLike).dispose?.(); } catch { /* swallow */ } }); } catch { /* swallow */ }
    }

    // Stale UI_PANE_REFRESH listener belongs to the prior build; drop it so the
    // new one owns the sync. The footer DOM (actions + domain) is preserved.
    const existingRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (existingRefs) {
        try { existingRefs.offRefresh(); } catch { /* swallow */ }
    }

    bindSlider(folder, params, 'fieldLineStep', { label: 'Step Size', min: 0.01, max: 1.0, step: 0.01 });
    bindSlider(folder, params, 'fieldLineMaxSteps', { label: 'Max Steps', min: 100, max: 50000, step: 100 });

    const dispatchFlDisplay = (rebuild = false): void => {
        store.dispatch(INTENTS.SET_FIELDLINE_DISPLAY, { rebuildGeometry: !!rebuild });
    };
    bindSlider(folder, params, 'fieldLineOpacity', { label: 'Opacity', min: 0.1, max: 1.0, step: 0.01 }).on('change', () => dispatchFlDisplay());
    if (developerMode) {
        folder.addBinding(params, 'fieldLineTubes', { label: '3D Tubes' }).on('change', () => dispatchFlDisplay());
        bindSlider(folder, params, 'fieldLineRadius', { label: 'Radius', min: 0.005, max: 0.2, step: 0.005 }).on('change', () => dispatchFlDisplay(true));
        folder.addBinding(params, 'fieldLineMaterial', { label: 'Material', options: { Standard: 'standard', Lambert: 'lambert', Phong: 'phong' } }).on('change', () => dispatchFlDisplay(true));
        folder.addBinding(params, 'fieldLineDepthWrite', { label: 'Depth Write' }).on('change', () => dispatchFlDisplay());
        folder.addBinding(params, 'fieldLineDepthTest', { label: 'Depth Test' }).on('change', () => dispatchFlDisplay());
        bindSlider(folder, params, 'fieldLineRenderOrder', { label: 'Render Order', min: -10, max: 10, step: 1 }).on('change', () => dispatchFlDisplay());
        folder.addBinding(params, 'fieldLineCastShadow', { label: 'Cast Shadow' }).on('change', () => dispatchFlDisplay());
        folder.addBinding(params, 'fieldLineReceiveShadow', { label: 'Receive Shadow' }).on('change', () => dispatchFlDisplay());
    }

    folder.addBlade?.({ view: 'separator' });

    // Footer DOM — Trace/Clear action tiles + scope-only domain strip. Preserved
    // across rebuilds via the WeakMap; the section registry fires a 'fieldlines'
    // rebuild on selection add/delete/relabel, so we just re-render the tiles.
    const folderBodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (folderBodyEl) {
        // Defensive: drop any orphan footer DOM that isn't the tracked one (folder
        // identity change via HMR / manual refs reset would otherwise accumulate).
        folderBodyEl.querySelectorAll<HTMLElement>('.pane-actions, .domain-strip').forEach((orphan) => {
            if (orphan !== existingRefs?.actions && orphan !== existingRefs?.domain.container) orphan.remove();
        });
    }
    const actions = existingRefs?.actions ?? buildActionTiles([
        { icon: 'fa-bezier-curve', label: 'Trace', onClick: () => store.dispatch(INTENTS.TRACE_FIELDLINE) },
        { cls: 'is-clear', icon: 'fa-trash', label: 'Clear', onClick: () => store.dispatch(INTENTS.CLEAR_FIELDLINES) },
        { id: 'visibility', icon: 'fa-eye-slash', label: 'Hide', onClick: () => store.dispatch(INTENTS.SET_FIELDLINES_VISIBILITY, { visible: !params['showFieldLines'] }) },
    ]);
    syncVisibilityTile(actions, 'visibility', !!params['showFieldLines'], 'field lines');
    let domain = existingRefs?.domain;
    if (!domain) {
        domain = buildDomainStrip('fieldlines');
    } else {
        renderDomainStrip(domain.stripEl, 'fieldlines');
    }

    const offRefresh = store.onIntent(INTENTS.UI_PANE_REFRESH, () => {
        syncDomainSelected('fieldlines');
        syncVisibilityTile(actions, 'visibility', !!params['showFieldLines'], 'field lines');
    });
    CUSTOM_REFS_BY_FOLDER.set(folder, { actions, domain, offRefresh });

    // Pin the footer to the bottom of the folder body. `appendChild` is a
    // move-or-insert: on rebuilds these already live in the body and get moved
    // past the freshly-mounted pane children. Domain strip last (bottom).
    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (bodyEl) {
        bodyEl.appendChild(actions);
        bodyEl.appendChild(domain.container);
    }

    if (pane && pane.refresh) pane.refresh();
    return refs;
}
